import {
  CampaignOverviewChildSteps,
  ConfirmationChildSteps,
  GiftConfigChildSteps,
  GiftDistributionChildSteps,
  ParentSteps
} from '@console/common/constants';
import { getActiveStep } from '@console/common/getActiveStep';
import { isGifteeBoxGiftConfig } from '@console/common/projectDraft/giftConfig';
import CampaignOverview from '@console/containers/projectDraft/CampaignOverviewContainer';
import ConfirmationContainer from '@console/containers/projectDraft/ConfirmationContainer';
import ContentsSelectionContainer from '@console/containers/projectDraft/ContentsSelectionContainer';
import DeliveryConfigContainer from '@console/containers/projectDraft/DeliveryConfigContainer';
import GiftConfigsContainer from '@console/containers/projectDraft/GiftConfigsContainer';
import GiftDistributionContainer from '@console/containers/projectDraft/GiftDistributionContainer';
import GiftTicketTypeConfigContainer from '@console/containers/projectDraft/GiftTicketTypeConfigContainer';
import MessageCardConfigContainer from '@console/containers/projectDraft/MessageCardConfigContainer';
import MessageCardUsingContainer from '@console/containers/projectDraft/MessageCardUsingContainer';
import type {
  RootPropsMappedFromDispatch,
  RootPropsMappedFromState
} from '@console/containers/projectDraft/RootContainer';
import { Navigate, Route, useLocation } from 'react-router';

type RootProps = RootPropsMappedFromState & RootPropsMappedFromDispatch;

export const isCampaignOverview = (
  parentStep: number,
  childStep: number
): boolean =>
  parentStep === ParentSteps.CAMPAIGN_OVERVIEW &&
  childStep === CampaignOverviewChildSteps.CAMPAIGN_OVERVIEW;

export const isGiftDistribution = (
  parentStep: number,
  childStep: number
): boolean =>
  parentStep === ParentSteps.GIFT_DISTRIBUTION &&
  childStep === GiftDistributionChildSteps.GIFT_DISTRIBUTION;

export const isGiftConfigs = (parentStep: number, childStep: number): boolean =>
  parentStep === ParentSteps.GIFT_CONFIG &&
  childStep === GiftConfigChildSteps.GIFT_CONFIGS;

const isTicketTypeConfig = (parentStep: number, childStep: number): boolean =>
  parentStep === ParentSteps.GIFT_CONFIG &&
  childStep === GiftConfigChildSteps.GIFT_TICKET_TYPE_CONFIG;

export const isContentsSelection = (
  parentStep: number,
  childStep: number
): boolean =>
  parentStep === ParentSteps.GIFT_CONFIG &&
  childStep === GiftConfigChildSteps.CONTENTS_SELECTION;

export const isDeliveryConfig = (
  parentStep: number,
  childStep: number
): boolean =>
  parentStep === ParentSteps.GIFT_CONFIG &&
  childStep === GiftConfigChildSteps.DELIVERY_CONFIG;

const isMessageCardUsing = (parentStep: number, childStep: number): boolean =>
  parentStep === ParentSteps.GIFT_CONFIG &&
  childStep === GiftConfigChildSteps.MESSAGE_CARD_USING;

export const isMessageCardConfig = (
  parentStep: number,
  childStep: number
): boolean =>
  parentStep === ParentSteps.GIFT_CONFIG &&
  childStep === GiftConfigChildSteps.MESSAGE_CARD_CONFIG;

const isConfirmation = (parentStep: number, childStep: number): boolean =>
  parentStep === ParentSteps.CONFIRMATION &&
  childStep === ConfirmationChildSteps.CONFIRMATION;

const Root: React.FC<RootProps> = ({ giftConfigs, selectedGiftConfig }) => {
  const location = useLocation();
  const { parentStep, childStep } = getActiveStep();

  const redirectToStep = (parentStep: ParentSteps, childStep: number) => (
    <Route>
      <Navigate
        to={`${location.pathname}?parentStep=${parentStep}&childStep=${childStep}`}
        replace
      />
    </Route>
  );

  if (isCampaignOverview(parentStep, childStep)) {
    return <CampaignOverview />;
  }
  if (isGiftDistribution(parentStep, childStep)) {
    return <GiftDistributionContainer />;
  }
  if (isGiftConfigs(parentStep, childStep)) {
    return <GiftConfigsContainer />;
  }
  if (giftConfigs.length === 0) {
    return redirectToStep(
      ParentSteps.GIFT_CONFIG,
      GiftConfigChildSteps.GIFT_CONFIGS
    );
  }
  if (isTicketTypeConfig(parentStep, childStep)) {
    return <GiftTicketTypeConfigContainer />;
  }
  if (isContentsSelection(parentStep, childStep)) {
    return <ContentsSelectionContainer />;
  }
  if (isDeliveryConfig(parentStep, childStep)) {
    return <DeliveryConfigContainer />;
  }
  if (isMessageCardUsing(parentStep, childStep)) {
    if (isGifteeBoxGiftConfig(selectedGiftConfig)) {
      return redirectToStep(
        ParentSteps.GIFT_CONFIG,
        GiftConfigChildSteps.GIFT_CONFIGS
      );
    }
    return <MessageCardUsingContainer />;
  }
  if (isMessageCardConfig(parentStep, childStep)) {
    if (isGifteeBoxGiftConfig(selectedGiftConfig)) {
      return redirectToStep(
        ParentSteps.GIFT_CONFIG,
        GiftConfigChildSteps.GIFT_CONFIGS
      );
    }
    return <MessageCardConfigContainer />;
  }
  if (isConfirmation(parentStep, childStep)) {
    return <ConfirmationContainer />;
  }
  return (
    <Route>
      <Navigate
        to={`${location.pathname}?parentStep=${ParentSteps.CAMPAIGN_OVERVIEW}&childStep=${CampaignOverviewChildSteps.CAMPAIGN_OVERVIEW}`}
        replace
      />
    </Route>
  );
};

export default Root;
